import React, { useEffect, useState } from 'react';
import ConfigureNotifications from '@/components/Modals/ConfigureNotifications';
import { useLoading } from '@/contexts/LoadingContext';
import MainContainer from '@/layouts/MainContainer';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ICommunicationGroups } from '@/types/api/communications';
import { handleErrorAsync } from '@/utils/handlers';
import { NotificationsActiveOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  ButtonProps,
  Card,
  Divider,
  Typography,
} from '@mui/material';

interface IOptionNotification {
  title: string;
}

const OptionNotification = ({
  title,
  ...props
}: IOptionNotification & ButtonProps) => {
  return (
    <>
      <Box
        sx={{
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="body2">{title}</Typography>
        <Button {...props} sx={{ p: 0 }}>
          acessar
        </Button>
      </Box>
    </>
  );
};

const ResetConfigureNotification = () => {
  const { setIsLoading } = useLoading();

  const [
    currentConfigureNotificationsModal,
    setCurrentConfigureNotificationsModal,
  ] = useState<string | null>(null);

  const [notificationGroups, setNotificationGroups] = useState<
    ICommunicationGroups[] | undefined
  >();

  const [idNotification, setIdNotification] = useState<number>();

  const handleResetNotifications = async () => {
    const [_, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      await api.post('/api/reset_communication_groups/');
    }).finally(() => setIsLoading(false));

    if (error) return Toast('error', error.message);

    Toast('success', 'Notificações restauradas com sucesso!');
  };

  useEffect(() => {
    const handleNotificationGroups = async () => {
      const [groups, error] = await handleErrorAsync(async () => {
        setIsLoading(true);
        const { data } = await api.get<ICommunicationGroups[]>(
          '/api/communication_groups/'
        );

        return data;
      });

      if (error) return Toast('error', error.message);

      setNotificationGroups(groups);
    };

    handleNotificationGroups().finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConfigureNotificationsModal]);

  const CardContent = () => {
    return (
      <Card sx={{ p: 2, gap: 2, display: 'grid', borderRadius: '12px' }}>
        {notificationGroups?.map((group, index) => {
          return (
            <React.Fragment key={group.id}>
              <OptionNotification
                title={group.name}
                onClick={() => {
                  setCurrentConfigureNotificationsModal(group.code);
                  setIdNotification(group.id);
                }}
              />
              {notificationGroups.length > 1 &&
                index !== notificationGroups.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </Card>
    );
  };

  return (
    <>
      <Box sx={{ backgroundColor: 'background.default', height: '100%' }}>
        <MainContainer
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Alert
            severity="info"
            icon={<NotificationsActiveOutlined fontSize="large" />}
            sx={{
              width: '100%',
              color: 'primary.dark',
              backgroundColor: 'primary.light',
              '& .MuiAlert-icon': { color: 'primary.dark' },
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="caption">
              <span style={{ fontWeight: 'bold' }}>
                Personalize sua experiência!
              </span>{' '}
              Configure as notificações do sistema conforme suas preferências
              para receber alertas relevantes e manter-se informado de maneira
              personalizada.
            </Typography>
          </Alert>
          <CardContent />
          <Box
            display="flex"
            width="100%"
            justifyContent="center"
            marginTop={2}
          >
            <Button
              sx={{ width: 'max-content' }}
              onClick={handleResetNotifications}
            >
              restaurar notificações padrão
            </Button>
          </Box>
        </MainContainer>
      </Box>

      <ConfigureNotifications
        handleClose={() => setCurrentConfigureNotificationsModal(null)}
        currentModal={currentConfigureNotificationsModal}
        idNotification={idNotification}
      />
    </>
  );
};

export default ResetConfigureNotification;
